import React, { useEffect, useState } from "react";
import {
  DAY_TO_NUMBER,
  DAYS,
  formatHourRange,
  HOUR_OPTIONS,
} from "../../constants";
import ReactSelect from "../../../ReactSelect";

type HourlyPrices = {
  [dayNumber: string]: {
    [hour: string]: number;
  };
};

interface Props {
  openingHour: number | null;
  closingHour: number | null;
  selectedDays: string[];
  hourlyPrices: HourlyPrices;
  setOpeningHour: (v: number | null) => void;
  setClosingHour: (v: number | null) => void;
  setSelectedDays: (v: string[]) => void;
  setHourlyPrices: React.Dispatch<React.SetStateAction<HourlyPrices>>;
}

const getHourSlots = (start: number, end: number) => {
  const hours: number[] = [];
  for (let h = start; h <= end; h++) hours.push(h);
  return hours;
};

const DynamicPricing: React.FC<Props> = ({
  openingHour,
  closingHour,
  selectedDays,
  hourlyPrices,
  setOpeningHour,
  setClosingHour,
  setSelectedDays,
  setHourlyPrices,
}) => {
  const [basePrice, setBasePrice] = useState<number>(1);
  useEffect(() => {
  if (
    openingHour === null ||
    closingHour === null ||
    closingHour <= openingHour ||
    selectedDays.length === 0
  ) {
    setHourlyPrices({});
    return;
  }

  const hours = getHourSlots(openingHour, closingHour);
  const next: HourlyPrices = {};

  selectedDays.forEach((day) => {
    const dayNumber = DAY_TO_NUMBER[day];
    next[dayNumber] = {};

    hours.forEach((hour) => {
      next[dayNumber][hour] = basePrice;
    });
  });

  setHourlyPrices(next);
}, [openingHour, closingHour, selectedDays, basePrice]);


  return (
    <div className="col-span-full border border-neutral-400 rounded-lg p-4 mt-4">
      <h3 className="text-base font-semibold mb-4">Dynamic Pricing Window</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ReactSelect
          label="Opening Hour"
          options={HOUR_OPTIONS}
          value={HOUR_OPTIONS.find((o) => o.value === openingHour) || null}
          onChange={(o: any) => setOpeningHour(o?.value ?? null)}
        />

        <ReactSelect
          label="Closing Hour"
          options={HOUR_OPTIONS}
          value={HOUR_OPTIONS.find((o) => o.value === closingHour) || null}
          onChange={(o: any) => setClosingHour(o?.value ?? null)}
        />
      </div>

      <div className="mt-4">
        <ReactSelect
          label="Select Days"
          options={DAYS}
          isMulti = {true}
          value={DAYS.filter((d) => selectedDays.includes(d.value)) as any}
          onChange={(opts: any) =>
            setSelectedDays((opts || []).map((o: any) => o.value))
          }
        />
      </div>
       <div className="mt-4 max-w-xs">
        <label className="block text-sm font-medium mb-1">
          Base Price
        </label>
        <input
          type="number"
          step="0.1"
          className="w-full border rounded px-2 py-1"
          value={basePrice}
          onChange={(e) => setBasePrice(Number(e.target.value))}
        />
      </div>

      {openingHour !== null &&
        closingHour !== null &&
        selectedDays.length > 0 && (
          <div className="overflow-x-auto mt-6">
            <table className="border-collapse border w-full text-xs">
              <thead>
                <tr>
                  <th className="border px-3 py-2 bg-gray-100">Day</th>
                  {getHourSlots(openingHour, closingHour).map((hour) => (
                    <th key={hour} className="border px-3 py-2 bg-gray-100">
                      {formatHourRange(hour)}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {selectedDays.map((day) => {
                  const dayNumber = DAY_TO_NUMBER[day];
                  return (
                    <tr key={day}>
                      <td className="border px-3 py-2 font-semibold">{day}</td>
                      {getHourSlots(openingHour, closingHour).map((hour) => (
                        <td key={hour} className="border px-2 py-1">
                          <input
                            type="number"
                            step="0.1"
                            className="w-12 rounded px-1 py-0.5"
                            value={hourlyPrices?.[dayNumber]?.[hour] ?? 1}
                            onChange={(e) => {
                              const value = Number(e.target.value);
                              setHourlyPrices((prev) => ({
                                ...prev,
                                [dayNumber]: {
                                  ...prev[dayNumber],
                                  [hour]: value,
                                },
                              }));
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
};

export default DynamicPricing;
